import React, { useEffect, useState } from "react";
import axios,{AxiosRequestHeaders} from "axios";
import { Link } from "react-router-dom";
import loaderimage from "../../../images/giphy.gif";
import ConfirmationModal from "../../shared/ConfirmationModal";
import NewconfirmationModal from "../../shared/NewconfirmationModal"; 
import AllFailureModal from "../../shared/AllFailureModal"; 

interface Companies {
  company_id: string;
  company_name: string;
  team_size: number;
  email: string;
  established_in: string;
  contact_number: string;
  primary_industry: string;
  company_website: string;
  comp_registration_number: string;
  about_company: string;
  country: string;
  state: string;
  city: string;
  address: string;
  zip_code: string;
  facebook: string;
  twitter: string;
  linkedin: string;
  whatsapp: string;
  instagram: string;
  pinterest: string;
  userid: string
}

const CompaniesList = () => {
  const [companieslist, setCompanies] = useState<Companies[]>([]);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [companyToDelete, setCompanyToDelete] = useState<string | null>(null);
  const [successModalShow, setSuccessModalShow] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureModalShow, setFailureModalShow] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState<Companies[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayTotal, setDisplayTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const headers: AxiosRequestHeaders = {
    'x-access-token': localStorage.getItem('jwttoken') || '',
  };
  const fetchCompaniesList = async (page = 1) => {
    try {
      setLoader(true);
      setError(null);
      const url = `${process.env.REACT_APP_API_URL}companyDetails/companylist?page=${page}&limit=${pageSize}`;
      const response = await axios.get(url,{headers});
      setCompanies(response.data.companies);
      setFilteredCompanies(response.data.companies);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalCompanies(response.data.companies.length);
      setDisplayTotal(response.data.companies.length);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setError("Failed to fetch companies. Please try again later.");
    } finally {
      setLoader(false);
    }
  };

  const handleDeleteConfirmation = async (isConfirmed: boolean) => {
    setModalShow(false);
    if (isConfirmed && companyToDelete) {
      try {
        setLoader(true);
        const url = `${process.env.REACT_APP_API_URL}companyDetails/removecompany/${companyToDelete}`;
        await axios.put(url);
        setCompanyToDelete(null);
        setSuccessMessage("Company deleted successfully!");
        setSuccessModalShow(true);
        fetchCompaniesList(currentPage);
      } catch (error) {
        setFailureMessage("Failed to delete company. Please try again.");
        setFailureModalShow(true);
      } finally {
        setLoader(false);
      }
    }
  };

  const confirmDelete = (companyId: string) => {
    setCompanyToDelete(companyId);
    setModalMessage("Are you sure you want to delete this company?");
    setModalShow(true);
  };

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm === "") {
        setFilteredCompanies(companieslist);
        setDisplayTotal(companieslist.length);
      } else {
        const filtered = companieslist.filter(
          (company) =>
            company.company_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            company.primary_industry
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            company.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCompanies(filtered);
        setDisplayTotal(filtered.length);
      }
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, companieslist]);

  useEffect(() => {
    fetchCompaniesList();
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page: number) => {
    fetchCompaniesList(page);
  };

  const startEntry = (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalCompanies);
    const currentPageEntries = filteredCompanies.length;
  const actualEndEntry = startEntry + currentPageEntries - 1;

  return (
    <>
      <div className="">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-black fw-bold">Company Management</h2>
          <div className="d-flex gap-2 align-items-center w-50" style={{ width: "510px" }}>
            <span className="fw-bold text-secondary text-nowrap">{filteredCompanies.length} results</span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by Name"
              value={searchTerm}
              onChange={handleSearch}
            />
            <Link to="/companiesadd" className="btn bg-info text-white">
              + Add Company
            </Link>
          </div>
        </div>

        <div className="card shadow-sm border-0 rounded">
          <div className="card-header bg-info border-bottom py-3">
            <h5 className="mb-0 text-white">Companies List</h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive" sticky-table-container>
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light position-sticky top-0" >
                  <tr>
                    <th>#</th>
                    <th>Company Name</th>
                    <th>Email</th>
                    <th>Team Size</th>
                    <th>Industry</th>
                    <th>Website</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan={10} className="text-center py-2">
                        <img src={loaderimage} alt="Loading..." height="40" />
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={10} className="text-center text-danger fw-bold py-3">
                        <div className="alert alert-danger text-center m-3" role="alert">
                          Oops! Something went wrong. Please try again later.
                        </div>
                      </td>
                    </tr>
                  ) : filteredCompanies.length > 0 ? (
                    filteredCompanies.map((item, index) => (
                      <tr key={item.company_id} className="border-bottom">
                        <td className="text-center">{startEntry + index}</td>
                        <td>{item.company_name}</td>
                        <td>{item.email}</td>
                        <td className="text-center">{item.team_size}</td>
                        <td>{item.primary_industry}</td>
                        <td>
                          <a
                            href={item.company_website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-info"
                          >
                            {item.company_website}
                          </a>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Link
                              to={`/companiesview/${item.company_id}/${item.userid}`}
                              className="btn btn-sm btn-outline-success"
                            >
                              View
                            </Link>
                            <Link
                              to={`/companiesedit/${item.company_id}`}
                              className="btn btn-sm btn-outline-warning"
                            >
                              Edit
                            </Link>
                            <button
                              onClick={() => confirmDelete(item.company_id)}
                              className="btn btn-sm btn-outline-danger"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center text-muted fw-bold">
                        <div className="alert alert-secondary text-center m-3" role="alert">
                          No companies available
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="position-sticky bottom-0 bg-white py-2 border-top">
              <div className="d-flex justify-content-between align-items-center px-3">
                <div className="flex-grow-1 text-muted">
                  Showing {filteredCompanies.length > 0 ? startEntry : 0}-{filteredCompanies.length > 0 ? startEntry + filteredCompanies.length - 1 : 0} of {filteredCompanies.length} entries
                </div>
                <nav>
                  <ul className="pagination mb-0 pe-3">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {totalPages > 0 && [...Array(totalPages)].map((_, index) => (
                      <li 
                        key={index} 
                        className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                      >
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>

        <ConfirmationModal
          show={modalShow}
          modalMessage={modalMessage}
          onHide={() => setModalShow(false)}
          confirmation={handleDeleteConfirmation}
          operationType="Delete"
        />

        <NewconfirmationModal
          show={successModalShow}
          onHide={() => setSuccessModalShow(false)}
          modalMessage={successMessage}
          operationType="OK"
          timeoutDuration={3000}
        />

        <AllFailureModal
          show={failureModalShow}
          onHide={() => setFailureModalShow(false)}
          modalMessage={failureMessage}
          operationType="OK"
          timeoutDuration={3000}
        />
      </div>
    </>
  );
};

export default CompaniesList;
